<template>
  <div
    ref="widgetContainer"
    class="f-h configurable-page"
    :class="{ 'configurable-page--template-edition': cfgTemplateEdition }"
    :style="containerStyle"
    @click="closeConfig"
  >
    <!-- Grid to snap widgets to it -->
    <Grid
      v-if="editable && page.snapWidgetsToGrid"
      :space="page.snapWidgetsToGrid"
      custom-class="cfg-page__grid-anchor"
      @click="closeConfig"
    />

    <!-- Main widget container -->
    <div v-if="mounted" class="f-w f-h">
      <Widget
        v-for="widget in statePage.widgets"
        ref="widget"
        :key="widget.id"
        :state-widget="$store.state.cfgPagesWidgets[`${widget.id}`]"
        :overlay="{ display: editable, content: widgetDisplayNames[widget.componentName] }"
        :editable="editable"
        :container="$refs.widgetContainer"
        :class="{ selected: widgetConfigTarget && widgetConfigTarget.widget.id == widget.id }"
        :max-z-index="maxWidgetsZIndex + maxStickersZIndex + 1"
        :cfg-template-edition="cfgTemplateEdition"
        :snap-selector="snapSelector"
        @edit="editWidget"
        @updateOptions="updateWidgetConfig"
        @moveOnTop="movingWidgetOnTop"
        @dragOrResizeDone="dragOrResizeDone"
        @delete="deleteWidget(widget)"
      />
      <Sticker
        v-for="sticker in stickers"
        v-show="!editable"
        :key="sticker.id"
        :sticker="sticker"
        :container="$refs.widgetContainer"
        :php-params="{ pageId: pageId }"
        :z-index="maxWidgetsZIndex + sticker.zIndex"
        :max-z-index="maxStickersZIndex + maxWidgetsZIndex + 1"
        @edit="stickerEdit"
      />
    </div>
    <!-- Bin to destroy stickers -->
    <div ref="bin" class="bin-area generic-border generic-border--thin-border">
      <div class="droppable-bin">
        <Icon class="f-w f-h">
          {{ deleteMedium }}
        </Icon>
      </div>
    </div>
    <div v-if="!page.widgets || !page.widgets.length" class="hint">
      {{ $t('message.CFG_PAGES_HINT_ADD_WIDGET') }}
    </div>

    <!-- Page edition.  -->
    <FiltersZone
      v-if="editable"
      class="configurable-page__filters-zone"
      :force-open="panelDisplayed"
      no-options-button
      disable-click-outside
      custom-modal-class="configurable-page__options-modal"
      @close="panelDisplayed = false"
    >
      <template v-slot:buttons>
        <button
          class="configurable-page__filters-zone__button generic-border generic-border--small-text-btn"
          @click.stop="openConfig()"
        >
          <Icon>{{ options }}</Icon>
          <span>{{ $t('message.SETTINGS') }}</span>
        </button>
      </template>
      <div class="widget__title">
        <div class="v-centerer" @click="configViewMode === 2 ? configViewMode = 0 : closeConfig()">
          <Icon
            :class="configViewMode === 2 ? 'widget__title__arrow-icon ' : 'widget__title__close-icon '"
          >
            {{ configViewMode === 2 ? arrowDownThin : close }}
          </Icon>
          <p v-if="configViewMode === 2">{{ $t('message.BACK') }}</p>
          <p v-else>{{ $t('message.CLOSE') }}</p>
        </div>
        <Switcher
          v-show="configViewMode !== 2"
          v-model="configViewMode"
          class="ml10em"
          :titles="configModes"
        />
      </div>
      <div v-show="configViewMode === 0">
        <div class="configurable-page__search generic-border generic-border--thin-border generic-border--focus-shadow">
          <Icon>{{ searchMedium }}</Icon>
          <input
            v-model="searchInput"
            type="search"
            :placeholder="$t('message.SEARCH_PH')"
            @input="function(e) { searchInput = e.target.value }"
          >
        </div>
        <div
          v-for="(widget, index) in allWidgets"
          :key="index"
          class="widget-holder mt10"
        >
          <div class="widget editable" :data-index="index">
            <div class="widget-overlay h-v-centerer">
              <div class="wo-content">
                {{ widgetDisplayNames[widget.componentName] }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-show="configViewMode === 1">
        <!-- Page config -->
        <h2 class="basic-sub-title">
          {{ $t('message.CFG_PAGE_CONFIG') }}
        </h2>
        <div class="configurable-page__main-config">
          <EditableLabel
            v-model="page.name"
            :use-textarea="true"
            :legend="$t('message.CFG_PAGE_NAME') + $t('message.TWO_DOTS')"
            @input="pageSettingsEdited"
          />
          <div v-if="!cfgTemplateEdition">
            <div class="configurable-page__main-config__in-menu">
              <IntCheckbox
                v-model="page.inMenu"
                class="mt5"
                :title="$t('message.CFG_PAGE_ADD_MENU')"
                @input="pageSettingsEdited"
              />
            </div>
            <RouterLink
              class="
                generic-border
                generic-border--text-btn
                generic-border--blue
                configurable-page__main-config__preview
              "
              :to="`/configurable-page?pageId=${pageId}`"
              target="_blank"
            >
              <Icon>{{ eyeMedium }}</Icon>
              <span>{{ $t('message.CFG_PAGE_PREVIEW') }}</span>
            </RouterLink>
            <button
              v-if="currentTeam && currentTeam.allowCfgTemplateFromTeam"
              class="
                generic-border
                generic-border--text-btn
                generic-border--blue
                configurable-page__main-config__template
              "
              @click="turnIntoTemplate"
            >
              <Icon>{{ uploadCloud }}</Icon>
              <span>{{ $t('message.CFG_PAGE_MAKE_TEMPLATE') }}</span>
            </button>
          </div>
          <div v-else>
            <IntCheckbox
              v-if="cfgTemplateEdition"
              v-model="page.locked"
              class="mt5"
              :title="$t('message.CFG_PAGE_LOCK_IMPORT')"
              @input="pageSettingsEdited"
            />
            <div class="field-hint">
              {{ $t('message.CFG_PAGE_LOCK_IMPORT_HINT') }}
            </div>
            <IntCheckbox
              v-if="cfgTemplateEdition"
              v-model="page.synced"
              class="mt5"
              :title="$t('message.CFG_PAGE_SYNCED')"
              @input="pageSettingsEdited"
            />
            <div class="field-hint">
              {{ $t('message.CFG_PAGE_SYNCED_HINT') }}
            </div>
          </div>
          <div class="legend mt10em">
            {{ `${$t('message.CFG_PAGE_SNAPPING')}${$t('message.TWO_DOTS')}` }}
          </div>
          <RadioGroup v-model="snapWidgetsToGrid" :items="snapWidgetOptions" class="config-panel__snap-opts">
            <div slot="1" class="v-centerer">
              <span class="legend mr3em ml3em">
                {{ `${$t('message.CFG_PAGE_SNAPPING_SPACE')}` }}
              </span>
              <input
                v-model="snapGridSpace"
                class="config-panel__snap-opts__space"
                type="number"
                @blur="pageSettingsEdited"
              >
              <span>
                {{ $t('message.PIXELS').toLowerCase() }}
              </span>
            </div>
          </RadioGroup>
          <div class="field-hint">
            {{ `${$t('message.MINIMUM')}${$t('message.TWO_DOTS')}${minimumSnapGridSpace}` }}
          </div>
        </div>
        <h2 class="basic-sub-title">
          {{ $t('message.CFG_PAGE_COLOR') }}
        </h2>
        <SColorPicker
          v-model="page.bgColor"
          @input="pageSettingsEdited"
        />
      </div>
      <div v-if="configViewMode === 2">
        <!-- Widget config -->
        <h2 class="basic-sub-title">
          {{ $t('message.CFG_PAGE_WIDGET_CONFIG') }}
        </h2>
        <div class="v-centerer configurable-page__options-modal__buttons">
          <button
            v-if="widgetConfigTarget"
            class="generic-border generic-border--text-btn generic-border--blue-border"
            @click="duplicateWidget(widgetConfigTarget)"
          >
            <Icon>{{ copyMedium }}</Icon>
            <span>{{ $t('message.CFG_PAGE_DUPLICATE_WIDGET') }}</span>
          </button>
          <button
            v-if="widgetConfigTarget"
            class="generic-border generic-border--text-btn generic-border--red"
            @click="deleteWidget(widgetConfigTarget.widget)"
          >
            <Icon>{{ deleteMedium }}</Icon>
            <span>{{ $t('message.WIDGET_DELETE') }}</span>
          </button>
        </div>
        <WidgetConfigPanel
          v-if="widgetConfigTarget"
          ref="configPanel"
          v-model="widgetConfig.options"
          @editionStart="widgetConfig.editionStart"
          @edit="widgetConfigTarget.configUpdated"
        />
        <div v-else style="padding: 0px 20px">
          {{ $t('message.CFG_PAGE_NO_WIDGET_SELECTED') }}
        </div>
      </div>
    </FiltersZone>
  </div>
</template>

<script>
/**
 * Blank page customizable at will by the user.
 * User may add any widgets on the screen and see a preview of what the real page will be.
 * ID of the configurable page to be edited is passed as a query param
 */

import $ from 'jquery';
import { cloneDeep } from 'lodash';
// State
import {
  FETCH_MENU_CFG_PAGES,
  FETCH_CFG_PAGE,
  SET_CFG_PAGE_WIDGET,
  SET_CFG_PAGE,
  DELETE_CFG_PAGE_WIDGET,
  FETCH_STICKERS,
  SET_STICKER,
  DELETE_STICKER,
  CREATE_CFG_PAGE,
} from '@/store/action-types';
import { GET_STICKERS } from '@/store/mutation-types';
// Components
import EditableLabel from '@/components/editable-label.vue';
import FiltersZone from '@/components/filters-zone.vue';
import Grid from '@/components/grid.vue';
import Icon from '@/components/icon.vue';
import IntCheckbox from '@/components/int-checkbox.vue';
import RadioGroup from '@/components/radio-group.vue';
import SColorPicker from '@/components/s-color-picker.vue';
import Sticker from '@/components/sticker.vue';
import Switcher from '@/components/switcher.vue';
import Widget from '@/components/widgets/widget.vue';
import WidgetConfigPanel from '@/components/widgets/widget-config-panel.vue';
// Utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
import { calert } from '@/utils/main';
import {
  arrowDownThin,
  close,
  copyMedium,
  deleteMedium,
  eyeMedium,
  options,
  searchMedium,
  uploadCloud,
} from '@/assets/icons/';

/** Slideshow widgets can be resource hogs and are not meant to be used multiple times */
const MAX_SLIDESHOW_WIDGETS = 2;

export default {

  name: 'ConfigurablePage',

  components: {
    EditableLabel,
    FiltersZone,
    Grid,
    Icon,
    IntCheckbox,
    RadioGroup,
    SColorPicker,
    Sticker,
    Switcher,
    Widget,
    WidgetConfigPanel,
  },

  mixins: [mixinSocketHandler],

  props: {
    cfgTemplateEdition: Boolean,
    displayGwWidgets: Boolean,
  },

  data() {
    return {
      arrowDownThin,
      close,
      copyMedium,
      deleteMedium,
      eyeMedium,
      options,
      searchMedium,
      uploadCloud,
      mounted: false,
      // Page data
      page: {},
      widgetDisplayNames: {
        ActionPlanWidget: this.$t('message.ACTION_PLAN'),
        DetailedIndicatorWidget: this.$t('message.DETAILED_INDICATOR'),
        GwCompletedListWidget: this.$t('message.GW_COMPLETE'),
        GwStatsWidget: this.$t('message.GW_STATS'),
        ImagesWidget: this.$t('message.IMAGES_AND_DOCS'),
        IndicatorWidget: this.$t('message.INDICATOR'),
        InformationsWidget: this.$t('message.INFORMATIONS'),
        PagesSlideshowWidget: this.$t('message.WIDGET_NAME_PAGES_SLIDESHOW'),
        PendingWidget: this.$t('message.WIDGET_NAME_PENDING'),
        ProblemsWidget: this.$t('message.WIDGET_PB_LIST'),
        StatsWidget: this.$t('message.STATS'),
        TextWidget: this.$t('message.WIDGET_NAME_TEXT'),
        TextPerDateWidget: this.$t('message.WIDGET_NAME_TEXT_PER_DATE'),
        ViewerWidget: this.$t('message.WIDGET_NAME_VIEWER'),
        ButtonLinkWidget: this.$t('message.WIDGET_NAME_BUTTON'),
        WheelWidget: this.$t('message.WIDGET_NAME_WHEEL'),
        StickersWidget: this.$t('message.WIDGET_NAME_STICKERS'),
      },
      // Search through widgets
      searchInput: '',
      panelDisplayed: false,
      widgetConfigTarget: null,
      widgetConfig: {},
      // Whether a left panel widget is being dragged
      dragging: false,
      configViewMode: 0,
      configModes: [this.$t('message.WIDGETS'), this.$t('message.PAGE')],
      openConfigAfterResize: false,
      openWidgetsAfterResize: false,
      // Stack index of the components added on the page (last one updated is the first)
      containerWidth: 0,
      containerHeight: 0,
      // Cfg pages may contain a Stickers Widget that allows the user to add stickers to the page
      stickers: [],
      preventDroppingStickerOnPage: false,
      // Options to snap widgets to each other or to a grid
      snapWidgetOptions: [
        { id: 0, label: this.$t('message.CFG_PAGE_SNAP_EACH_OTHER') },
        { id: 1, label: this.$t('message.CFG_PAGE_SNAP_GRID') },
      ],
      minimumSnapGridSpace: 5,
      defaultSnapGridSpace: 20,
      pageSettingsEditionTimeout: null,
      // Properties used by other components. See src/views/readme.md
      pageTitle: '',
      pageBackButtonAction: () => {
        this.$router.back();
      },
    };
  },

  computed: {
    containerStyle() {
      const color = this.page.bgColor && this.page.bgColor.match(/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/)
        ? this.page.bgColor
        : '#FFFFFF';
      return {
        backgroundColor: color,
      };
    },
    /** Maximum zIndex value for a widget */
    maxWidgetsZIndex() {
      const indexes = (this.page.widgets || []).map(w => w.zIndex);
      return indexes.length ? Math.max(...indexes) : 0;
    },
    /** Maximum zIndex value for a sticker */
    maxStickersZIndex() {
      const indexes = this.stickers.map(s => s.zIndex);
      return indexes.length ? Math.max(...indexes) : 1;
    },
    /** Number of slideshow widgets on this page */
    nSlideshowWidgets() {
      return this.page.widgets.filter(w => w.componentName === 'PagesSlideshowWidget').length;
    },
    /** Cfg page from Vuex */
    statePage() {
      this.$nextTick(() => {
        if (
          this.editable
          && this.$store.state.cfgPages[`${this.pageId}`]
          && this.$store.state.cfgPages[`${this.pageId}`].widgets
          && !this.$store.state.cfgPages[`${this.pageId}`].widgets.length
        ) {
          this.openConfig();
        }
      });
      return this.$store.state.cfgPages[`${this.pageId}`] || {};
    },
    /** All available widgets */
    allWidgets() {
      const baseWidgets = [
        { componentName: 'ActionPlanWidget' },
        { componentName: 'DetailedIndicatorWidget' },
        { componentName: 'ImagesWidget' },
        { componentName: 'IndicatorWidget' },
        { componentName: 'InformationsWidget' },
        { componentName: 'PagesSlideshowWidget' },
        { componentName: 'PendingWidget' },
        { componentName: 'ProblemsWidget' },
        { componentName: 'StatsWidget' },
        { componentName: 'TextWidget' },
        { componentName: 'TextPerDateWidget' },
        { componentName: 'ViewerWidget' },
        { componentName: 'ButtonLinkWidget' },
        { componentName: 'WheelWidget' },
        { componentName: 'StickersWidget' },
      ];
      const gwWidgets = [
        { componentName: 'GwCompletedListWidget' },
        { componentName: 'GwStatsWidget' },
      ];
      if (
        (this.currentTeam && this.currentTeam.accessGW === 1)
        || (this.cfgTemplateEdition && this.displayGwWidgets)
      ) {
        baseWidgets.push(...gwWidgets);
      }

      const filteredOut = this.cfgTemplateEdition ? ['PagesSlideshowWidget', 'WheelWidget'] : [];
      const effectiveSearch = this.searchInput.sAccentless.toUpperCase().split(/[\s,-./']/);
      return baseWidgets
        .filter(w => !filteredOut.includes(w.componentName))
        .filter((widget) => {
          const name = this.widgetDisplayNames[widget.componentName];
          return !effectiveSearch.some(term => !name.sAccentless.toUpperCase().includes(term));
        });
    },
    /** Boolean: Whether the page currently displayed is in its editable state */
    editable() {
      return this.$route.query.editable === 'true';
    },
    /** Biilean: Whether the page currently displayed is in its editable state */
    pageId() {
      return Number(this.$route.query.pageId);
    },
    /** Number: 0: snap widgets to each other, 1: snap widgets to a grid */
    snapWidgetsToGrid: {
      get() {
        return this.page.snapWidgetsToGrid === null || this.page.snapWidgetsToGrid <= 0 ? 0 : 1;
      },
      set(newVal) {
        if (newVal === 1 && this.page.snapWidgetsToGrid === null) {
          this.page.snapWidgetsToGrid = this.defaultSnapGridSpace;
          this.pageSettingsEdited();
        } else if (newVal === 0 && this.page.snapWidgetsToGrid !== null) {
          this.page.snapWidgetsToGrid = 0;
          this.pageSettingsEdited();
        }
      },
    },
    /** Number: Value of the offset between each line of the grid */
    snapGridSpace: {
      get() {
        return this.page.snapWidgetsToGrid === null ? 0 : this.page.snapWidgetsToGrid;
      },
      set(newVal) {
        this.page.snapWidgetsToGrid = Number(newVal);
      },
    },
    /** String: HTML selector used in jQuery draggable 'snap' option */
    snapSelector() {
      return this.page.snapWidgetsToGrid ? '.cfg-page__grid-anchor' : '.widget.widget-component';
    },
  },

  watch: {
    page(newVal) {
      if (newVal.name) {
        this.updatePageTitle();
      }
    },
    panelDisplayed(newVal) {
      if (newVal) {
        if (this.configViewMode === 0) {
          // Setup draggable behavior on widgets panel open
          this.$nextTick(this.setupDraggable);
        }
      } else {
        // Removing draggable behavior on widgets panel close since the components are destroyed
        const inst = $('.widget-holder .widget').draggable('instance');
        if (inst) {
          $('.widget-holder .widget').draggable('destroy');
        }
      }
    },
    configViewMode(newVal) {
      if (newVal === 0 && this.panelDisplayed) {
        const inst = $('.widget-holder .widget').draggable('instance');
        if (!inst) {
          this.$nextTick(this.setupDraggable);
        }
      }
    },
    statePage(newVal) {
      this.page = $.extend(true, {}, newVal);
    },
    pageId(newVal, oldVal) {
      if (this.mounted && newVal !== oldVal) {
        this.refresh();
      }
    },
    displayGwWidgets() {
      this.$nextTick(this.setupDraggable);
    },
    snapSelector() {
      // Snap option changed, update draggable
      this.setupDraggable();
    },
  },

  beforeRouteUpdate(to, from, next) {
    next();
    if (to.query.pageId === from.query.pageId) {
      this.refreshWidgets();
    }
  },

  mounted() {
    this.mounted = true;
    this.refresh();
    const container = this.$refs.widgetContainer;
    this.containerWidth = container.offsetWidth;
    this.containerHeight = container.offsetHeight;
    $(window).on(`resizeEnd.${this.uuid}`, () => {
      this.containerWidth = container.offsetWidth;
      this.containerHeight = container.offsetHeight;
      this.refreshWidgetsSize();
    });

    // Listen to new widgets or widget removals
    this.handleSocketMessage(SocketMessages.CC_MSGS.CFG_PAGE, (msg) => {
      if (msg.id === this.pageId) {
        this.refresh();
      }
    });
    // Listen to stickers changes
    this.handleSocketMessage(SocketMessages.CC_MSGS.CFG_PAGE_STICKERS, (msg) => {
      if (msg.id === this.pageId) {
        this.$store.dispatch(FETCH_STICKERS, { pageId: this.pageId }).then(this.setStickers);
      }
    });
  },

  beforeDestroy() {
    let inst = $('.widget-holder .widget').draggable('instance');
    if (inst) {
      $('.widget-holder .widget').draggable('destroy');
    }
    inst = $(this.$refs.widgetContainer).droppable('instance');
    if (inst) {
      $(this.$refs.widgetContainer).droppable('destroy');
    }
    inst = $(this.$refs.bin).droppable('instance');
    if (inst) {
      $(this.$refs.bin).droppable('destroy');
    }
  },

  methods: {
    updatePageTitle() {
      this.pageTitle = this.page.name;
    },
    /** Get the widgets, set zIndex and draggable */
    refresh() {
      this.$store.dispatch(FETCH_CFG_PAGE, { id: this.pageId });
      if (this.editable) {
        this.$nextTick(this.setupDraggable);
      }
      this.$nextTick(this.setupDroppable);
      this.$store.dispatch(FETCH_STICKERS, { pageId: this.pageId }).then(this.setStickers);
    },
    /** Clone stickers from state */
    setStickers() {
      this.stickers = cloneDeep(this.$store.state[GET_STICKERS.stateKey] || []);
    },
    setupDraggable() {
      $('.widget-holder .widget').draggable({
        helper: 'clone',
        containment: this.$refs.widgetContainer,
        addClasses: false,
        snap: this.snapSelector,
        snapTolerance: 7,
        revert: 'invalid',
        start: (e, ui) => {
          ui.helper.dataset = e.target.dataset;
          $(ui.helper).css('width', `${ui.helper.prevObject[0].offsetWidth}px`);
          $(ui.helper).css('height', `${ui.helper.prevObject[0].offsetHeight}px`);
        },
      });
    },
    duplicateWidget(duplicate) {
      const cWidth = this.$refs.widgetContainer.clientWidth;
      const cHeight = this.$refs.widgetContainer.clientHeight;
      const { width, height, componentName } = duplicate.widget;
      let { x, y } = duplicate.widget;
      // Calculating where the widget is on the page, to avoid duplicated widget going out of the container
      // For the width
      if (cWidth * width / 100 + cWidth * (Number(x) + 3) / 100 >= cWidth) {
        x = (Number(x) - 3).toString();
      } else {
        x = (Number(x) + 3).toString();
      }
      // For the height
      if (cHeight * height / 100 + cHeight * (Number(y) + 3) / 100 >= cHeight) {
        y = (Number(y) - 3).toString();
      } else {
        y = (Number(y) + 3).toString();
      }
      const zIndex = this.maxWidgetsZIndex + 1;
      const { componentData } = duplicate.widget;
      this.$store.dispatch(SET_CFG_PAGE_WIDGET, {
        page: this.pageId,
        x,
        y,
        width,
        height,
        zIndex,
        componentData,
        componentName,
      }).then((data) => {
        this.$nextTick(() => {
          const newWidget = this.$refs.widget.find(component => component.widget && component.widget.id === data.id);
          if (newWidget && newWidget.emitEdition && this.widgetConfigTarget) {
            newWidget.emitEdition(true);
          }
        });
      });
    },
    setupDroppable() {
      // Destroy stickers
      $(this.$refs.bin).droppable({
        accept: '.colored-sticker',
        tolerance: 'pointer',
        drop: (e, ui) => {
          const el = ui.helper[0];
          const { id } = el.dataset;
          if (!id) {
            // New sticker do nothing, prevent dropping it on the page
            this.preventDroppingStickerOnPage = true;
          } else {
            calert(this.$t('message.CONFIRM_DELETE_STICKER'), [
              {
                title: this.$t('message.DELETE'),
                action: () => {
                  this.deleteSticker(id);
                },
                sClass: 'red',
              },
              {
                title: this.$t('message.CANCEL'),
              },
            ]);
          }
        },
      });

      $(this.$refs.widgetContainer).droppable({
        accept: '.widget-holder .widget, .sticker .colored-sticker',
        drop: (e, ui) => {
          if (ui.draggable.hasClass('colored-sticker') && this.preventDroppingStickerOnPage) {
            // Sticker has already been dropped on the bin area
            this.preventDroppingStickerOnPage = false;
            return;
          }
          const el = ui.helper[0];
          const container = this.$refs.widgetContainer;
          const elRect = el.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const index = el.dataset.index || 0;
          const widget = this.allWidgets[index];
          const width = el.offsetWidth * 100 / this.containerWidth;
          const height = el.offsetHeight * 100 / this.containerHeight;
          const x = (elRect.left - containerRect.left) * 100 / this.containerWidth;
          const y = (elRect.top - containerRect.top) * 100 / this.containerHeight;

          if (ui.helper.hasClass('widget')) {
            // Dropping a widget
            if (
              widget.componentName === 'PagesSlideshowWidget'
              && MAX_SLIDESHOW_WIDGETS <= this.nSlideshowWidgets
            ) {
              calert(this.$t('message.SLIDESHOW_LIMIT_REACHED'), [{
                title: this.$t('message.CANCEL'),
              }]);
              return;
            }

            this.$store.dispatch(SET_CFG_PAGE_WIDGET, {
              page: this.pageId,
              x,
              y,
              width,
              height,
              zIndex: this.maxWidgetsZIndex + 1,
              bgColor: '#ffffff',
              componentName: widget.componentName,
            }).then(() => {
              this.sendSocketMessage({ msg: SocketMessages.CC_MSGS.CFG_PAGE, id: this.pageId });
            });
          } else {
            // Dropping a sticker
            const color = el.dataset.colIndex;
            const rect = { x, y, width: width * 3, height: height * 3 };
            this.$store.dispatch(SET_STICKER, { pageId: this.pageId, rect, color }).then(() => {
              this.setStickers();
              this.stickerEdit();
            });
          }
        },
      });
    },
    /** Call XHR to remove a sticker from the page */
    deleteSticker(id) {
      this.$store.dispatch(DELETE_STICKER, { id, pageId: this.pageId }).then(() => {
        this.setStickers();
        this.stickerEdit();
      });
    },
    /** One or several stickers have been edited */
    stickerEdit() {
      this.setStickers();
      this.sendSocketMessage({
        msg: SocketMessages.CC_MSGS.CFG_PAGE_STICKERS,
        id: this.pageId,
      });
    },
    /** Edited name or inMenu */
    pageSettingsEdited() {
      clearTimeout(this.pageSettingsEditionTimeout);
      this.pageSettingsEditionTimeout = setTimeout(() => {
        const params = {
          id: this.pageId,
          name: this.page.name,
          inMenu: this.page.inMenu,
          bgColor: this.page.bgColor,
          locked: this.page.locked,
          synced: this.page.synced,
          snapWidgetsToGrid: this.page.snapWidgetsToGrid,
        };
        this.$store.dispatch(SET_CFG_PAGE, params).then(() => {
          this.update++;
          this.$store.dispatch(FETCH_MENU_CFG_PAGES);
        });
      }, 200);
    },
    /** Confirmation received that the widget is to be deleted */
    deleteWidget(widget) {
      calert(this.$t('message.WIDGET_CONFIRM_DELETE'), [
        {
          title: this.$t('message.YES'),
          sClass: 'red',
          action: () => {
            this.confirmDeleteWidget(widget);
          },
        },
        {
          title: this.$t('message.NO'),
        },
      ]);
    },
    /** User confirmed the deletion of the widget */
    confirmDeleteWidget(widget) {
      this.$store.dispatch(DELETE_CFG_PAGE_WIDGET, { pageId: this.pageId, id: widget.id })
        .then(() => {
          this.sendSocketMessage({ msg: SocketMessages.CC_MSGS.CFG_PAGE, id: this.pageId });
          if (this.widgetConfigTarget && (this.widgetConfigTarget.widget.id === widget.id)) {
            this.closeConfig();
            this.widgetConfigTarget = null;
          }
        });
    },
    /** A widget is moving, put it on top of the others */
    movingWidgetOnTop(widget) {
      if (this.panelDisplayed) {
        this.panelDisplayed = false;
        this.openWidgetsAfterResize = true;
      }
      widget.zIndex = this.maxWidgetsZIndex + 1;
    },
    /** Drag or resize of a widget is over */
    dragOrResizeDone() {
      if (this.openWidgetsAfterResize) {
        this.openWidgetsAfterResize = false;
        this.openConfig();
      }
    },
    /** Call refresh on widget components */
    refreshWidgets() {
      const widgetToUpdate = Number(this.$route.query.widgetToUpdate);
      this.$refs.widget.forEach((w) => {
        if (typeof w.refresh === 'function' && w.widget.id === widgetToUpdate) {
          w.refresh();
        }
      });
    },
    /** Widget edition triggered */
    editWidget(widget, config) {
      this.widgetConfigTarget = widget;
      this.widgetConfig = config;
      this.openConfig();
      this.configViewMode = 2;
    },
    /** The displayed widget config (if it is displayed) needs to be updated */
    updateWidgetConfig(widget, config) {
      if (this.panelDisplayed && this.widgetConfigTarget.id === widget.id) {
        this.widgetConfig = config;
      }
    },
    /** Opening config panel, close widgets panel if opened */
    openConfig() {
      this.panelDisplayed = true;
    },
    /** Un-select a widget */
    closeConfig() {
      // If a widget is selected and the panel is closed, unselect the widget
      if (this.widgetConfigTarget && !this.panelDisplayed) {
        this.widgetConfigTarget = null;
        this.widgetConfig = {};
      // If a widget is selected, and the panel is open, close the panel only
      } else if (this.widgetConfigTarget || this.panelDisplayed) {
        this.configViewMode = 0;
        this.panelDisplayed = false;
      }
    },
    /** Propagate window size change event to widgets */
    refreshWidgetsSize() {
      (this.$refs.widget || []).forEach((w) => {
        w.sizeChanged();
      });
    },
    /** Add this page to the config templates list */
    turnIntoTemplate() {
      this.$store.dispatch(CREATE_CFG_PAGE, { templateFromPage: this.pageId });
    },
    userLoaded() {
      (this.$refs.widget || []).forEach((w) => {
        if (typeof w.refresh === 'function') {
          w.userLoaded();
        }
      });
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.configurable-page {
  background: #ffffff;
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;

  &__main-config {
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    flex-direction: column;

    & .editable-label {
      width: 100%;
      & > textarea {
        height: auto !important;
      }
    }

    &__preview, &__template {
      margin-bottom: .5rem;

      &.generic-border--text-btn {
        display: inline-flex;
      }
    }

    &__in-menu {
      margin: .5rem 0;
    }
  }

  // Override filters-zone main style
  &__filters-zone {
    position: absolute;
    top: 0px;
    right: 0px;

    & > .filters-zone__buttons {
      background: none;
    }
    &__button {
      box-shadow: $medium-box-shadow;
      // Putting a z-index here to allow the button to be over the Widgets.
      z-index: 10000;
    }
  }

  &__options-modal {
    .slide-fade-modal__background {
      display: none;
    }

    .slide-fade-modal__content {
      box-shadow: $light-box-shadow;
    }

    &__buttons {
      flex-wrap: wrap;

      > * {
        margin: .5rem;
      }
    }
  }

  &__search {
    display: flex;
    align-items: center;
    height: 40px;
    margin-top: 1rem;

    .icon {
      flex: 0 0 auto;
      width: 0.9rem;
    }

    input {
      width: calc(100% - 2.3rem);
      border: none;
      padding-left: 0.9rem;
      height: 36px;
      box-sizing: border-box;
      outline: none;
    }
  }

  #page-content {
    padding: 0px;
    background: #ffffff;
    overflow: hidden;
  }
  .hint {
    font-size: 1.1em;
    position: absolute;
    bottom: 40px;
    text-align: center;
    width: 100%;
  }

  .config-panel__snap-opts {
    &__space {
      width: 2em;
    }

    &.panel-above {
      z-index: 10007;
    }
    .content h2, .switcher {
      margin-left: 20px;
    }
  }
  /* Customizing compact-color-picker */
  .vc-compact {
    width: calc(100% - 30px);
    margin-top: 10px;
    padding: 0px;
    box-shadow: initial;
  }
  .vc-compact-color-item {
    margin: 0 1.6% 5px 0;
    width: 9.5%;
  }
  .vc-compact-dot {
    top: 4px;
    right: auto;
    bottom: auto;
    left: 50%;
    transform: translateX(-50%);
    width: 7px;
    height: 7px;
  }
  /* customizing expandable-settings-item */
  .settings-dropdown {
    margin-top: 5px;

    .settings-item {
      background: #E0E0E0;
      height: 30px;
      padding-left: 15px;

      .expand-img {
        height: 18px;
      }
    }
    .sd-overflow > div {
      padding: 0px 15px;

      .expandable-color-picker, .expandable-widget-design {
        margin: 5px -15px;
      }
    }
    .sd-overflow > span {
      padding: 0px 15px;
    }
  }

  .bin-area {
    position: absolute;
    bottom: 0.5em;
    left: 0.5em;
    width: 54px;
    height: 54px;
    opacity: 0;
    transition: 0.3s transform;
    color: white;

    &.ui-droppable-active {
      z-index: 10001;
      opacity: 1;
      background: $color-shizen-red;
    }

    .droppable-bin {
      width: 100%;
      height: 100%;
    }

    &.ui-droppable-hover {
      opacity: 1;
      transform: scale(1.25, 1.25);
    }
  }
}
.widget {
  background-color: $color-body-background;
  position: relative;
  border-radius: $high-border-radius;
  padding: 10px;
  box-sizing: border-box;

  &__title {
    margin-top: 2em;
    display: flex;
    align-items: center;
    justify-content: space-between;
    line-height: 3em;

    &__close-icon {
      width: 0.7em;
      margin: 0 0.5em;
    }

    &__arrow-icon {
      transform: rotate(90deg);
    }
  }

  &.nopadding {
    padding: 0px;
  }
  &.editable {
    cursor: pointer;
  }
  &.editable.ui-draggable-dragging {
    z-index: 13004;
  }
  &.selected {
    box-shadow: 0 0 0 1px $color-shizen-blue-btn !important;

    .ui-resizable-handle {
      &.widget__handle-se, &.widget__handle-sw, &.widget__handle-ne, &.widget__handle-nw {
        display: block !important;
      }
    }
  }

  .ui-resizable-handle {
    &.widget__handle-se, &.widget__handle-sw, &.widget__handle-ne, &.widget__handle-nw {
      border-radius: 0px;
      background: white;
      border: 1px solid $color-shizen-blue-btn;
      z-index: 13002 !important;
      display: none !important;
    }
  }

  &__handle-sw {
    left: -8px;
    bottom: -8px;
  }

  &__handle-se {
    right: -8px;
    bottom: -8px;
  }

  &__handle-nw {
    left: -8px;
    top: -8px;
  }

  &__handle-ne {
    right: -8px;
    top: -8px;
  }

  .widget-overlay {
    position: absolute;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;
    z-index: 13001;

    .wo-content {
      font-weight: 600;
      font-size: 1.1em;
      text-align: center;
    }
    .wo-help {
      position: absolute;
      bottom: 0px;
      left: 50%;
      transform: translateX(-50%);
      width: 100%;
      text-align: center;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      padding: 5px 20px;
      box-sizing: border-box;
      background: #ffffff;
      opacity: 0;
      transition: opacity 0.1s ease-out;
    }
  }
  .wo-head {
    height: 3.5em;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: #ffffffa0;
    text-align: center;
    opacity: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.1s ease-out;

    .wo-h-name {
      display: inline-flex;
      vertical-align: top;
      height: 32px;
    }
  }
  .wo-delete {
    top: 50%;
    right: 0.5em;
    transform: translateY(-50%);
    position: absolute;
  }
  &.widget-component .widget-overlay:hover {
    .wo-help {
      opacity: 1;
    }
    .wo-head {
      opacity: 1;
    }
  }
}
.widget-holder {
  .widget {
    min-height: 70px;
  }
}

@media (max-width: $smartphone-landscape) {
  .configurable-page {
    .settings-dropdown {
      .sd-overflow > div {
        padding: 0px 5px;

        .expandable-color-picker, .expandable-widget-design {
          margin: 5px -5px;
        }
      }
    }
  }
}
</style>
